import { damayaUrl } from '../../seo/pricing';
import { asset } from '../../lib/assets';
import BeforeAfter from '../../components/feature/BeforeAfter';
import FramePair from '../../components/feature/FramePair';
import HeroVideo from '../../components/feature/HeroVideo';
import { Link } from '../../components/base/Link';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Home() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section: full-bleed street scene, real pipeline output */}
      <section className="relative overflow-hidden bg-brand-navy">
        <HeroVideo srcDesktop={asset('/media/hero-street-1080.mp4')} srcMobile={asset('/media/hero-street-720.mp4')} poster={asset('/media/hero-street-poster.jpg')} label={t('home.heroImageAlt')} />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/70 to-slate-950/25"></div>
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-950/80 to-transparent"></div>
        <div className="relative mx-auto flex min-h-[560px] max-w-7xl flex-col justify-center px-6 py-24 lg:min-h-[640px]">
          <div className="max-w-2xl text-white">
            <h1 className="text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">{t('home.heroTitle')}</h1>
            <p className="mt-6 text-xl text-cyan-200 md:text-2xl">{t('home.heroSubtitle')}</p>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-blue-100">{t('home.heroDesc')}</p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <a href={damayaUrl} className="whitespace-nowrap rounded-lg bg-cyan-500 px-8 py-4 text-center text-lg font-semibold text-slate-900 transition-colors hover:bg-cyan-400">
                {t('common.tryNow')}
              </a>
              <Link to="/pricing" className="whitespace-nowrap rounded-lg border border-white/30 bg-white/10 px-8 py-4 text-center text-lg font-semibold text-white transition-colors hover:bg-white/20">
                {t('home.viewDemo')}
              </Link>
            </div>
          </div>
          <p className="absolute bottom-3 left-6 right-6 text-right text-[11px] leading-snug text-blue-200/70 sm:left-auto sm:whitespace-nowrap">{t('home.heroImageCaption')}</p>
        </div>
      </section>

      {/* 两条路径 */}
      <section id="products" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('home.pathTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('home.pathDesc')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <article className="bg-white rounded-2xl shadow-card p-8 flex flex-col">
              <div className="flex items-center gap-4">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white ring-1 ring-gray-200"><img src={asset('/images/brand/damaya-logo.webp')} alt="" aria-hidden="true" width="40" height="40" loading="lazy" className="h-10 w-10 object-contain" /></span>
                <div>
                  <p className="text-sm font-semibold text-brand-700">{t('home.pathDamayaBadge')}</p>
                  <h3 className="mt-1 text-2xl font-bold text-gray-900">{t('home.pathDamayaTitle')}</h3>
                </div>
              </div>
              <p className="mt-4 text-gray-600 leading-relaxed">{t('home.pathDamayaDesc')}</p>
              <ul className="mt-5 space-y-2 text-gray-600">
                <li className="flex items-start"><i className="ri-check-line text-brand-600 mr-2 mt-1"></i>{t('home.pathDamayaPoint1')}</li>
                <li className="flex items-start"><i className="ri-check-line text-brand-600 mr-2 mt-1"></i>{t('home.pathDamayaPoint2')}</li>
                <li className="flex items-start"><i className="ri-check-line text-brand-600 mr-2 mt-1"></i>{t('home.pathDamayaPoint3')}</li>
              </ul>
              <a href={damayaUrl} className="mt-8 inline-flex w-fit items-center rounded-lg bg-brand-600 px-6 py-3 font-semibold text-white hover:bg-brand-700 transition-colors whitespace-nowrap">
                {t('common.tryNow')}<i className="ri-external-link-line ml-2"></i>
              </a>
              <Link to="/damaya" className="mt-3 inline-flex w-fit items-center text-sm font-medium text-brand-700 hover:text-brand-600">{t('home.pathDamayaMore')}<i className="ri-arrow-right-line ml-1"></i></Link>
            </article>
            <article className="bg-white rounded-2xl shadow-card p-8 flex flex-col border-2 border-brand-600">
              <div className="flex items-center gap-4">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white ring-1 ring-gray-200"><img src={'/logo.svg'} alt="" aria-hidden="true" width="40" height="40" loading="lazy" className="h-10 w-10 object-contain" /></span>
                <div>
                  <p className="text-sm font-semibold text-brand-700">{t('home.pathApiBadge')}</p>
                  <h3 className="mt-1 text-2xl font-bold text-gray-900">{t('home.pathApiTitle')}</h3>
                </div>
              </div>
              <p className="mt-4 text-gray-600 leading-relaxed">{t('home.pathApiDesc')}</p>
              <ul className="mt-5 space-y-2 text-gray-600">
                <li className="flex items-start"><i className="ri-check-line text-brand-600 mr-2 mt-1"></i>{t('home.pathApiPoint1')}</li>
                <li className="flex items-start"><i className="ri-check-line text-brand-600 mr-2 mt-1"></i>{t('home.pathApiPoint2')}</li>
                <li className="flex items-start"><i className="ri-check-line text-brand-600 mr-2 mt-1"></i>{t('home.pathApiPoint3')}</li>
              </ul>
              <Link to="/pricing" className="mt-8 inline-flex w-fit items-center rounded-lg bg-brand-navy px-6 py-3 font-semibold text-white hover:bg-brand-900 transition-colors whitespace-nowrap">
                {t('home.contactBtn')}<i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </article>
          </div>

          <div className="mt-10 rounded-2xl border border-gray-200 bg-white p-6">
            <p className="text-sm font-semibold text-gray-900">{t('home.capsTitle')}</p>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <p className="text-gray-600"><span className="font-medium text-gray-900">{t('home.capsPrimaryLabel')}</span> {t('home.capsPrimary')}
                <Link to="/video-redaction" className="ml-2 text-brand-700 underline">{t('products.videoRedaction')}</Link>
                <Link to="/image-redaction" className="ml-2 text-brand-700 underline">{t('products.imageRedaction')}</Link>
              </p>
              <p className="text-gray-600"><span className="font-medium text-gray-900">{t('home.capsAssessLabel')}</span> {t('home.capsAssess')}
                <Link to="/text-redaction" className="ml-2 text-brand-700 underline">{t('products.textRedaction')}</Link>
                <Link to="/file-redaction" className="ml-2 text-brand-700 underline">{t('products.fileRedaction')}</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 真实能力条 */}
      <section className="py-16 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-3">{t('home.factsTitle')}</h2>
            <p className="text-blue-100">{t('home.factsDesc')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(['fact1', 'fact2', 'fact3', 'fact4', 'fact5', 'fact6'] as const).map(key => (
              <div key={key} className="rounded-xl border border-white/10 bg-white/5 p-5 flex items-start">
                <i className="ri-checkbox-circle-line text-brand-cyan text-xl mr-3 mt-0.5"></i>
                <p className="text-blue-50 leading-relaxed">{t(`home.${key}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 应用场景 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('home.scenariosTitle')}</h2>
            <p className="text-xl text-gray-600">
              {t('home.scenariosDesc')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <article className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-camera-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('home.scenario1Title')}</h3>
              <p className="text-gray-600">{t('home.scenario1Desc')}</p>
            </article>
            
            <article className="text-center">
              <div className="w-20 h-20 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-roadster-line text-3xl text-brand-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('home.scenario2Title')}</h3>
              <p className="text-gray-600">{t('home.scenario2Desc')}</p>
            </article>
            
            <article className="text-center">
              <div className="w-20 h-20 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-film-line text-3xl text-brand-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('home.scenario3Title')}</h3>
              <p className="text-gray-600">{t('home.scenario3Desc')}</p>
            </article>
            
            <article className="text-center">
              <div className="w-20 h-20 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-line text-3xl text-brand-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('home.scenario4Title')}</h3>
              <p className="text-gray-600">{t('home.scenario4Desc')}</p>
            </article>
          </div>
        </div>
      </section>

      {/* 产品示例展示：真实成片帧 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('home.demoTitle')}</h2>
            <p className="text-xl text-gray-600">{t('home.demoDesc')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <article className="bg-white rounded-xl shadow-lg overflow-hidden p-5">
              <BeforeAfter before={asset('/images/real/ai-crowd-before.webp')} after={asset('/images/real/ai-crowd-after.webp')} beforeLabel={t('common.before')} afterLabel={t('common.after')} alt={t('home.demoFaceTitle')} aspectClass="aspect-[3/2]" />
              <h3 className="mt-5 text-xl font-semibold text-gray-900">{t('home.demoFaceTitle')}</h3>
              <p className="mt-2 text-gray-600">{t('home.demoFaceDesc')}</p>
            </article>
            <article className="bg-white rounded-xl shadow-lg overflow-hidden p-5">
              <FramePair before={asset('/images/real/sign-before.webp')} after={asset('/images/real/sign-after.webp')} beforeLabel={t('common.before')} afterLabel={t('common.after')} alt={t('home.demoTextTitle')} />
              <h3 className="mt-5 text-xl font-semibold text-gray-900">{t('home.demoTextTitle')}</h3>
              <p className="mt-2 text-gray-600">{t('home.demoTextDesc')}</p>
            </article>
            <article className="bg-white rounded-xl shadow-lg overflow-hidden p-5">
              <FramePair before={asset('/images/real/playground-7-auto.webp')} after={asset('/images/real/playground-7-keep.webp')} beforeLabel={t('home.keepBefore')} afterLabel={t('home.keepAfter')} alt={t('home.demoKeepTitle')} portrait />
              <h3 className="mt-5 text-xl font-semibold text-gray-900">{t('home.demoKeepTitle')}</h3>
              <p className="mt-2 text-gray-600">{t('home.demoKeepDesc')}</p>
            </article>
          </div>

          <p className="mt-8 text-center text-sm text-gray-500">{t('common.sourceNote')} {t('common.aiNote')} {t('home.demoNote')}</p>

          <div className="text-center mt-10">
            <Link
              to="/examples"
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('home.viewMoreExamples')}
            </Link>
          </div>
        </div>
      </section>

      {/* 接入流程示意 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('home.apiTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('home.apiDesc')}</p>
          </div>
          <div className="grid gap-10 lg:grid-cols-2 items-start">
            <ol className="space-y-6">
              {(['1', '2', '3'] as const).map(n => (
                <li key={n} className="flex">
                  <span className="mr-4 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 font-bold text-brand-700">{n}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t(`home.apiStep${n}Title`)}</h3>
                    <p className="mt-1 text-gray-600 leading-relaxed">{t(`home.apiStep${n}Desc`)}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div>
              <pre className="overflow-x-auto rounded-xl bg-slate-900 p-6 text-sm leading-relaxed text-blue-100"><code>{`# 1. ${t('home.apiStep1Title')}
POST /v1/tasks
{ "type": "video", "source": "<your file url>",
  "targets": ["face", "plate"], "callback": "<your url>" }

# 2. ${t('home.apiStep2Title')}
GET  /v1/tasks/{id}
{ "status": "processing", "progress": 62 }

# 3. ${t('home.apiStep3Title')}
GET  /v1/tasks/{id}/result
{ "status": "done", "output": "<download url>", "objects": 149 }`}</code></pre>
              <p className="mt-3 text-sm text-gray-500">{t('home.apiNote')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-navy">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {t('home.ctaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/pricing#contact"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('home.freeTrialBtn')}
            </Link>
            <Link 
              to="/pricing"
              className="bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-800 transition-colors border border-blue-500 whitespace-nowrap cursor-pointer"
            >
              {t('home.contactBtn')}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
